import { z } from "zod";

/**
 * Vektor-Annotationen (Version 2): pro Seite eine Liste von Strichen.
 * Punkte sind normalisiert (0–1) relativ zur Seitengröße: [x, y, druck].
 */
export const STROKE_LIMITS = {
  maxPages: 500,
  maxStrokesPerPage: 2000,
  maxPointsPerStroke: 20_000,
  maxJsonBytes: 4 * 1024 * 1024,
} as const;

export const strokeToolSchema = z.enum(["pen", "highlighter", "eraser"]);

const round4 = (value: number) => Math.round(value * 10_000) / 10_000;
const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const pointSchema = z
  .array(z.number())
  .min(2)
  .max(3)
  .transform(([x = 0, y = 0, p]) => {
    const pressure = p !== undefined && Number.isFinite(p) && p > 0 && p <= 1 ? p : 0.8;
    return [round4(clamp01(x)), round4(clamp01(y)), round4(pressure)] as [number, number, number];
  });

export const strokeSchema = z
  .object({
    id: z.string().regex(/^[\w-]{1,48}$/),
    tool: strokeToolSchema,
    mode: z.enum(["draw", "erase"]).optional(),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    alpha: z.number().optional(),
    size: z.number().positive().max(0.2),
    hasPressure: z.boolean().optional(),
    points: z.array(pointSchema).min(1).max(STROKE_LIMITS.maxPointsPerStroke),
  })
  .transform((stroke, ctx) => {
    const mode = stroke.tool === "eraser" || stroke.mode === "erase" ? "erase" : "draw";
    if (mode === "draw" && !stroke.color) {
      ctx.addIssue({ code: "custom", message: "Strichfarbe fehlt" });
      return z.NEVER;
    }
    const alpha =
      stroke.alpha !== undefined && Number.isFinite(stroke.alpha) && stroke.alpha > 0 && stroke.alpha <= 1
        ? stroke.alpha
        : 1;
    return {
      id: stroke.id,
      tool: stroke.tool,
      mode,
      color: mode === "draw" ? (stroke.color as string).toUpperCase() : "#000000",
      alpha: round4(alpha),
      size: round4(stroke.size) || 0.0001,
      hasPressure: stroke.hasPressure === true,
      points: stroke.points,
    } as const;
  });

export const strokePageSchema = z.object({
  strokes: z.array(strokeSchema).max(STROKE_LIMITS.maxStrokesPerPage),
  pageWidth: z.number().positive().optional(),
  pageHeight: z.number().positive().optional(),
});

export const strokeDocumentSchema = z
  .object({
    version: z.literal(2),
    pages: z.record(z.string().regex(/^[1-9]\d{0,4}$/), strokePageSchema),
  })
  .refine((doc) => Object.keys(doc.pages).length <= STROKE_LIMITS.maxPages, "Zu viele annotierte Seiten")
  .transform((doc) => {
    const pages: Record<string, z.infer<typeof strokePageSchema>> = {};
    for (const [key, page] of Object.entries(doc.pages)) {
      if (!page.strokes.length) continue;
      pages[key] = {
        strokes: page.strokes,
        ...(page.pageWidth ? { pageWidth: Math.round(page.pageWidth * 100) / 100 } : {}),
        ...(page.pageHeight ? { pageHeight: Math.round(page.pageHeight * 100) / 100 } : {}),
      };
    }
    return { version: 2 as const, pages };
  });

export type Stroke = z.output<typeof strokeSchema>;
export type StrokeDocument = z.output<typeof strokeDocumentSchema>;
export type StrokeDocumentInput = z.input<typeof strokeDocumentSchema>;

export function emptyStrokeDocument(): StrokeDocument {
  return { version: 2, pages: {} };
}
